// src/components/Tenants/VoiceCallDialog.js
// @flow

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';

import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PersonIcon from '@mui/icons-material/Person';

import { useTheme } from '@mui/material/styles';

import { createClasses } from '../../utils/stylesUtils';
import voiceCallDialogStyles from './Styles/VoiceCallDialog.Style';
import VoiceService from '../../services/VoiceService';
import SPBackend from '../../services/SPBackend';
import { auth } from '../../services/firebase';
import { formatPhoneNumberForFrontend } from '../../utils/jsUtils';
import { setSnackMessage } from '../../reduxStore/modules/common';

import type { Tenant } from '../../services/SPBackend';

type Props = {
  open: boolean,
  setOpen: (val: boolean) => void,
  tenant: ?Tenant
};

type CallStatus = 'idle' | 'connecting' | 'ringing' | 'in-progress' | 'completed' | 'failed' | 'cancelled' | 'rejected' | 'provisioning';

const VoiceCallDialog = (props: Props) => {
  const { open, setOpen, tenant } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = createClasses(voiceCallDialogStyles, theme);

  // Call state
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Timer ref for call duration
  const timerRef = useRef<?number>(null);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start call duration timer
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  }, []);

  // Stop call duration timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Handle call status changes from VoiceService
  const handleStatusChange = useCallback((status: string, deviceError?: Error) => {
    console.log('[VoiceCallDialog] status changed:', status);
    setCallStatus(status);

    if (status === 'in-progress') {
      startTimer();
    } else if (['completed', 'failed', 'cancelled', 'rejected'].includes(status)) {
      stopTimer();
    }

    // Handle device errors (e.g., JWT validation failed, connection errors)
    if (status === 'error' && deviceError) {
      const errorMessage = deviceError.message || 'Connection error occurred';
      console.error('[VoiceCallDialog] device error:', errorMessage);
      setError(errorMessage);
      setCallStatus('failed');
    }
  }, [startTimer, stopTimer]);

  // Initialize call when dialog opens
  const initializeCall = useCallback(async () => {
    if (!tenant) return;

    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setError('Authentication session expired. Please log in again.');
      setCallStatus('failed');
      return;
    }

    try {
      setCallStatus('connecting');
      setError('');
      setCallDuration(0);

      // Get voice token from backend
      let tokenResponse;
      try {
        tokenResponse = await SPBackend.getVoiceToken(firebaseUser);
      } catch (tokenError) {
        // Check if this is a "not provisioned" error
        if (tokenError.response?.data?.code === 'VOICE_NOT_PROVISIONED') {
          const accountId = tokenError.response.data.accountId;
          console.log('[VoiceCallDialog] voice not provisioned, auto-provisioning for account:', accountId);
          setCallStatus('provisioning');

          try {
            await SPBackend.provisionVoice(accountId, firebaseUser);
            console.log('[VoiceCallDialog] voice provisioned successfully, retrying token');
            // Retry getting token after provisioning
            tokenResponse = await SPBackend.getVoiceToken(firebaseUser);
            setCallStatus('connecting');
          } catch (provisionError) {
            console.error('[VoiceCallDialog] failed to provision voice:', provisionError);
            setError('Failed to set up voice calling. Please try again.');
            setCallStatus('failed');
            return;
          }
        } else {
          // Re-throw other errors
          throw tokenError;
        }
      }

      const { token } = tokenResponse.data;

      // Initialize VoiceService with token
      VoiceService.setStatusCallback(handleStatusChange);
      await VoiceService.initialize(token);

      // Initiate the call (creates message in conversation, returns IDs)
      const initiateResponse = await SPBackend.initiateVoiceCall(tenant._id, firebaseUser);
      const { conversationId, messageId, fromNumber, toNumber } = initiateResponse.data;

      // Connect the call
      await VoiceService.connect({
        To: toNumber,
        ConversationId: conversationId,
        MessageId: messageId,
        FromNumber: fromNumber
      });

    } catch (err) {
      console.error('[VoiceCallDialog] error initiating call:', err);
      const errorMessage = err?.message || 'Failed to initiate call. Please try again.';
      setError(errorMessage);
      setCallStatus('failed');
    }
  }, [tenant, handleStatusChange]);

  // Start call when dialog opens
  useEffect(() => {
    if (open && tenant && callStatus === 'idle') {
      initializeCall();
    }
  }, [open, tenant, callStatus, initializeCall]);

  // Cleanup on unmount or dialog close
  useEffect(() => {
    return () => {
      stopTimer();
      if (VoiceService.hasActiveCall()) {
        VoiceService.hangup();
      }
    };
  }, [stopTimer]);

  // Handle hang up
  const handleHangup = () => {
    VoiceService.hangup();
    setCallStatus('completed');
    stopTimer();
  };

  // Handle mute toggle
  const handleToggleMute = () => {
    const newMuteState = !isMuted;
    VoiceService.mute(newMuteState);
    setIsMuted(newMuteState);
  };

  // Handle dialog close
  const handleClose = () => {
    // If call is active, hang up first
    if (VoiceService.hasActiveCall()) {
      VoiceService.hangup();
    }

    // Clean up VoiceService
    VoiceService.destroy();

    // Reset state
    setCallStatus('idle');
    setCallDuration(0);
    setIsMuted(false);
    setError('');
    stopTimer();

    // Close dialog
    setOpen(false);

    // Show notification if call was completed
    if (callStatus === 'completed' && callDuration > 0) {
      dispatch(setSnackMessage({
        message: `Call ended (${formatDuration(callDuration)})`,
        level: 'info',
        duration: 3000
      }));
    }
  };

  // Get status display text
  const getStatusText = (): string => {
    switch (callStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'in-progress':
        return formatDuration(callDuration);
      case 'completed':
        return 'Call ended';
      case 'failed':
        return 'Call failed';
      case 'cancelled':
        return 'Call cancelled';
      case 'rejected':
        return 'Call rejected';
      case 'provisioning':
        return 'Setting up voice calling...';
      default:
        return '';
    }
  };

  // Check if call controls should be shown
  const showCallControls = callStatus === 'in-progress' || callStatus === 'ringing';
  const isCallEnded = ['completed', 'failed', 'cancelled', 'rejected'].includes(callStatus);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: classes.dialogPaper
      }}
    >
      <DialogTitle sx={classes.dialogTitle}>
        Voice Call
      </DialogTitle>

      <DialogContent>
        <Box sx={classes.contentContainer}>
          {/* Customer avatar */}
          <Avatar sx={classes.avatar}>
            <PersonIcon sx={classes.avatarIcon} />
          </Avatar>

          {/* Customer name */}
          <Typography variant="h6" gutterBottom>
            {tenant ? `${tenant.FirstName || ''} ${tenant.LastName || ''}`.trim() || 'Customer' : 'Customer'}
          </Typography>

          {/* Customer phone */}
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {tenant?.Phone ? formatPhoneNumberForFrontend(tenant.Phone) : 'No phone number'}
          </Typography>

          {/* Call status display */}
          <Box sx={classes.statusContainer}>
            {callStatus === 'connecting' && (
              <Box sx={classes.statusBox}>
                <CircularProgress size={32} sx={classes.progressSpinner} />
                <Typography variant="body1">Connecting...</Typography>
              </Box>
            )}

            {callStatus === 'provisioning' && (
              <Box sx={classes.statusBox}>
                <CircularProgress size={32} sx={classes.progressSpinner} />
                <Typography variant="body1">Setting up voice calling...</Typography>
              </Box>
            )}

            {callStatus === 'ringing' && (
              <Box sx={classes.statusBox}>
                <CallIcon sx={classes.pulseIcon} />
                <Typography variant="body1">Ringing...</Typography>
              </Box>
            )}

            {callStatus === 'in-progress' && (
              <Typography variant="h3" sx={classes.callDuration}>
                {formatDuration(callDuration)}
              </Typography>
            )}

            {isCallEnded && (
              <Typography variant="body1" color={callStatus === 'failed' ? 'error' : 'textSecondary'}>
                {getStatusText()}
              </Typography>
            )}

            {callStatus === 'failed' && (
              <Typography variant="body2" color="textSecondary" sx={classes.errorText}>
                Please try again. Contact us if the error persists.
              </Typography>
            )}
          </Box>

          {/* Call controls */}
          {showCallControls && (
            <Box sx={classes.controlsContainer}>
              {/* Mute button */}
              <IconButton
                onClick={handleToggleMute}
                sx={isMuted ? classes.muteButtonActive : classes.muteButton}
              >
                {isMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>

              {/* Hang up button */}
              <IconButton
                onClick={handleHangup}
                sx={classes.hangupButton}
              >
                <CallEndIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={classes.dialogActions}>
        <Button onClick={handleClose} variant={isCallEnded ? 'contained' : 'outlined'}>
          {showCallControls ? 'End & Close' : 'Close'}
        </Button>
      </DialogActions>

      {/* CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.1); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </Dialog>
  );
};

export default VoiceCallDialog;
